import React from 'react';
import styled from 'styled-components';

import { MediaUrl } from '@components/Flow/Nodes/types';
import { MediaParameter } from '@components/Modals/WorkflowRunMessage/MediaParameter';
import { Divider, Paper, Stack } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@common/Button';
import { AddOutlined, ImageOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../../../styles';

const MediaContainer = styled.div`
  width: 400px;
  padding: 1rem;
  max-height: 750px;
  overflow-y: auto;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  overflow: hidden;
  width: 100%;
  margin-top: 1rem;
`;

const Image = styled.img`
  aspect-ratio: 1;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line.light};
  object-fit: cover;
  height: 117px;
  overflow: hidden;
`;

interface Props {
  media: MediaUrl[];
  setMedia: React.Dispatch<React.SetStateAction<MediaUrl[]>>;
}

export const MessageMediaAttach: React.FC<Props> = ({ media, setMedia }) => {
  const theme = useTheme();

  const addDisabled = media.some((item) => item.value === '');

  const addMediaUrl = () => {
    setMedia((prev) => prev.concat({ id: uuidv4(), value: '' }));
  };

  const updateMediaUrl = (id: string, value: string) => {
    setMedia((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return { ...item, value: value };
        }

        return item;
      })
    );
  };

  const deleteMediaUrl = (id: string) => {
    setMedia((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <Paper
      sx={{
        backgroundColor: theme.background.standard,
        border: `1px solid ${theme.line.light}`,
      }}
    >
      <MediaContainer>
        <Stack direction='row' alignItems='center' spacing={1} mb={2}>
          <ImageOutlined />
          <h3>Add Media</h3>
        </Stack>
        <Stack spacing={1.5}>
          {media.map(({ id, value }) => (
            <MediaParameter
              key={id}
              id={id}
              value={value}
              updateMediaUrl={(newVal) => updateMediaUrl(id, newVal)}
              deleteMediaUrl={deleteMediaUrl}
              disabled={false}
            />
          ))}
          <Button disabled={addDisabled} color='primary' fullWidth size='large' onClick={addMediaUrl}>
            <AddOutlined sx={{ fontSize: '24px', color: `${theme.text.standard} !important` }} />
          </Button>
        </Stack>
        {media.length > 0 && <Divider sx={{ marginTop: '1rem' }} />}
        <ImageGrid>
          {media.map(
            ({ id, value }) => value && <Image key={id} src={value} theme={theme} alt={`Media Not Found (${value})`} />
          )}
        </ImageGrid>
      </MediaContainer>
    </Paper>
  );
};
